<template>
    <div>
        <!-- B-1 (UI-Gate Welle 2, welle2-ui-report.md): bei 768 px war die
             Tabelle 23-38 px breiter als ihr nächster scrollbarer Vorfahre
             (`main.overflow-y-auto` im Layout) — abgeschnittene Beträge
             ohne Hinweis. Ein eigener `overflow-x-auto`-Wrapper macht die
             Tabelle selbst scrollbar (sichtbare Affordanz, native
             Scrollbar) statt sich auf den Seiten-Scroll zu verlassen. -->
        <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 mt-2 md:mt-4">
                <thead class="bg-minplan-lightgreybeige">
                    <!-- UI-1 (UI-Gate, 2026-08-25): die Deckfarbe sitzt auf der
                         ZEILE und nicht nur auf `thead`. Die klebende
                         Total-Zelle uebernimmt sie per `bg-inherit`; eine
                         durchsichtige Zelle liesse die Spalten darunter
                         durchscrollen. -->
                    <tr class="bg-minplan-lightgreybeige">
                        <!-- "Alle auf-/zuklappen" sitzt IN der Kopfzeile der
                         Themen-Spalte, nicht in einer eigenen Zeile über der
                         Tabelle: dort schwebte es rechtsbündig über allem und
                         war mit keinem Text darüber bündig. Hier steht es
                         bündig mit der Spaltenüberschrift und dort, wo es
                         wirkt. `normal-case`/`tracking-normal` heben die
                         Kapitälchen der Kopfzeile für den Schalter wieder auf,
                         damit er als Aktion und nicht als zweite Überschrift
                         liest.

                         Farbe: `text-gray-900` statt des zunächst gewählten
                         `text-minplan-darkgreybeige`. Auf dem grauen Kopfband
                         (`#979694`) kam Letzteres auf 2,86 : 1 und las wie
                         abgeblendeter, inaktiver Text — über der Tabelle hatte
                         derselbe Schalter rund 12 : 1. `text-gray-900` liegt
                         bei 5,9 : 1 (UI-Gate V2-Nachlauf, Befund B-3). Die
                         Spaltenüberschriften daneben liegen mit 2,63 : 1
                         ebenfalls darunter — das ist unberührter Bestand und
                         gehört als eigener Befund erfasst, nicht hierher. -->
                        <th
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-left text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            <span class="inline-flex items-center gap-3">
                                {{ $t("apartments.topic") }}
                                <button
                                    v-if="expandableTopics.length"
                                    type="button"
                                    class="normal-case tracking-normal font-normal text-xs text-gray-900 hover:underline"
                                    @click="toggleAll"
                                >
                                    {{
                                        allExpanded
                                            ? $t("apartments.collapseAll")
                                            : $t("apartments.expandAll")
                                    }}
                                </button>
                            </span>
                        </th>
                        <th
                            v-if="show('budget')"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("apartments.budgetCHF") }}
                        </th>
                        <th
                            v-if="show('mehrkosten')"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t(offeredLabelKey) }}
                        </th>
                        <!-- Auftrag Luca, 2026-08-17: im budgetbasierten Modus
                         zurück, weil dort die Gebühr genau aus dieser
                         Differenz entsteht. Im Offerten-Modus bleibt sie
                         weg — dort hängen Budget und Gebühr nicht mehr
                         zusammen. -->
                        <th
                            v-if="showDifference"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("apartments.difference") }}
                        </th>
                        <th
                            v-if="show('gebuehr')"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("apartments.baseFee") }}
                        </th>
                        <th
                            v-if="show('honorar')"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("apartments.consultancyFee") }}
                        </th>
                        <!-- Auftrag Luca, 2026-08-25, Entscheide 4.1 bis 4.4:
                             das Total je Thema. Der Server rechnet es aus den
                             Summanden seines Modus und lässt es weg, wenn
                             keiner davon sichtbar ist — die Spalte hängt
                             deshalb allein daran, ob ein Wert kommt. -->
                        <th
                            v-if="showTotalColumn"
                            scope="col"
                            class="px-2 py-2 lg:py-3 text-right text-xs lg:text-sm font-medium text-minplan-beige uppercase tracking-wider"
                            :class="stickyTotalHead"
                        >
                            {{ $t("apartments.totalColumn") }}
                            <!-- K-4 (UI-Gate Fix-Runde, 2026-08-26): ohne
                                 `whitespace-nowrap` brach «inkl. MWST» bei
                                 1024 und 768 in zwei Zeilen um, und die
                                 Kopfzeile wuchs auf drei. Kostet nichts an
                                 Breite — die Spalte wird ohnehin vom Betrag
                                 bestimmt, nicht von der Beschriftung (im
                                 ersten Bericht mit exakt 0 px gemessen). -->
                            <span
                                class="block normal-case tracking-normal font-normal whitespace-nowrap"
                            >
                                {{
                                    mwst
                                        ? $t("apartments.inclVat")
                                        : $t("apartments.exclVat")
                                }}
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <template v-for="topic in topics" :key="topic.uuid">
                        <tr class="bg-white">
                            <td
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm font-medium text-gray-900"
                            >
                                <button
                                    v-if="hasModifications(topic)"
                                    type="button"
                                    class="topic-toggle mr-1 align-middle text-gray-500"
                                    :aria-expanded="isExpanded(topic).toString()"
                                    @click="toggle(topic)"
                                >
                                    <ChevronDownIcon
                                        v-if="isExpanded(topic)"
                                        class="h-4 w-4 inline"
                                    />
                                    <ChevronRightIcon v-else class="h-4 w-4 inline" />
                                </button>
                                <!-- Platzhalter exakt in der Breite des Aufklapp-
                                 Symbols (h-4 w-4 = 1rem, plus dessen `mr-1`),
                                 damit Themen OHNE Bestellungsänderungen auf
                                 derselben Kante beginnen wie die mit Symbol.
                                 Ohne ihn sprang jeder Themenname ohne Symbol
                                 um 20 px nach links. -->
                                <span
                                    v-else
                                    aria-hidden="true"
                                    class="inline-block w-4 mr-1 align-middle"
                                ></span>
                                <!-- B-1 Runde 2 (Fix-Runde 2, Nachprobe NP-01): lange
                                 Themennamen dürfen bei <lg das Budget nicht
                                 sprengen — Kürzung per `truncate`, voller Name
                                 über `title` weiterhin erreichbar. -->
                                <a
                                    :href="topic.specLink"
                                    :title="topic.name"
                                    class="hover:underline inline-block align-bottom truncate max-w-[5rem] lg:max-w-none"
                                    >{{ topic.name }}</a
                                >
                                <p
                                    v-if="topic.inclusive_budget > 0"
                                    class="text-xs text-minplan-darkgreybeige truncate max-w-[7rem] lg:max-w-none"
                                >
                                    {{ $t("apartments.includedInPurchasePrice") }}
                                    {{ topic.inclusive_budget_formatted }}
                                    <span
                                        v-if="topic.inclusive_perUnit != null"
                                        class="ml-1 text-minplan-darkgreybeige/50"
                                    >
                                        &#126; {{ topic.inclusive_perUnit }}
                                    </span>
                                </p>
                            </td>
                            <td
                                v-if="show('budget')"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-gray-900 text-right"
                            >
                                {{ fmt(rowBudget(topic)) }}
                            </td>
                            <td
                                v-if="show('mehrkosten')"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-gray-900 text-right"
                            >
                                {{ fmt(rowCost(topic)) }}
                            </td>
                            <td
                                v-if="showDifference"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-gray-900 text-right"
                            >
                                {{ fmt(rowMehrkosten(topic)) }}
                            </td>
                            <td
                                v-if="show('gebuehr')"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-gray-900 text-right"
                            >
                                {{ fmt(rowFee(topic)) }}
                            </td>
                            <td
                                v-if="show('honorar')"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-gray-900 text-right"
                            >
                                {{ fmt(rowConsultancyFee(topic)) }}
                            </td>
                            <td
                                v-if="showTotalColumn"
                                class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm font-medium text-gray-900 text-right"
                                :class="stickyTotal"
                            >
                                {{ fmt(rowTotal(topic)) }}
                            </td>
                        </tr>
                        <!-- Detailebene: eingerückte Zeilen der ÄUSSEREN Tabelle,
                         nicht eine verschachtelte Tabelle in einer
                         colspan-Zelle. Dadurch liegt der Betrag in der echten
                         "Offeriert"-Zelle und sitzt strukturell auf der
                         Spalte — die frühere Colspan-Arithmetik samt leerer
                         Füllzelle (K-2) entfällt ersatzlos. -->
                        <template v-if="isExpanded(topic) && hasModifications(topic)">
                            <tr
                                v-for="row in detailRows(topic)"
                                :key="`${topic.uuid}-${row.key}`"
                                :class="detailRowClass(row)"
                            >
                                <template v-if="row.type === 'separator'">
                                    <!-- UI-1, Nachtrag (code-reviewer,
                                         2026-08-26): der Trenner endet eine
                                         Spalte frueher und bekommt eine eigene
                                         klebende Zelle. Spannte er wie zuvor
                                         ueber ALLE Spalten, blieb im klebenden
                                         Band an dieser Zeile eine Luecke in
                                         Rand und Schatten — die einzige Zeile
                                         der Tabelle ohne klebende Zelle. -->
                                    <td
                                        :colspan="
                                            showTotalColumn
                                                ? visibleColumnCount - 1
                                                : visibleColumnCount
                                        "
                                        class="pl-6 lg:pl-12 pr-2 py-1 text-[10px] lg:text-xs uppercase tracking-wider text-gray-400"
                                    >
                                        {{ row.label }}
                                    </td>
                                    <td v-if="showTotalColumn" :class="stickyTotal"></td>
                                </template>
                                <template v-else>
                                    <td
                                        class="pl-6 lg:pl-12 pr-2 py-1.5 text-xs lg:text-sm text-gray-600"
                                    >
                                        <div
                                            class="flex items-center gap-2"
                                            :class="detailContentClass(row)"
                                        >
                                            <!-- UI-Gate B-1 (V2-detailebene-ui-report.md):
                                             `w-16` (64 px) fasst das Datum nur bei
                                             12-px-Schrift — ab `lg` rendert es mit
                                             14 px auf ~74 px und stiess ohne
                                             `whitespace-nowrap` in den Titel
                                             ("12.08.2026test"). Kasten wächst ab
                                             `lg` mit der Schrift mit. -->
                                            <span
                                                class="w-16 lg:w-20 shrink-0 whitespace-nowrap"
                                                :class="
                                                    row.dimmed
                                                        ? 'text-gray-400'
                                                        : 'text-gray-700'
                                                "
                                                >{{ row.mod.date }}</span
                                            >
                                            <!-- UI-Gate B-2 / R-1 (V2-detailebene-ui-report.md
                                             Abschnitte 6 und 8): `truncate` setzt
                                             `white-space: nowrap`; unter
                                             `table-layout: auto` bestimmt damit die
                                             UNGEKÜRZTE Textbreite die Spalte — ein
                                             langer Titel schob bei 1024/768 alle
                                             Wertspalten aus dem Bild (bis 433 px
                                             Überhang). Erst die Obergrenze deckelt die
                                             Intrinsic-Breite und macht die Kürzung
                                             wirksam; der volle Titel bleibt über
                                             `title` und den Link erreichbar.
                                             Die Werte sind aus der Überhang-Messreihe
                                             des Gates zurückgerechnet, nicht geschätzt
                                             (Plateau-Überhang minus gesetztem Deckel =
                                             verfügbares Titelbudget): 768 ≈ 66 px →
                                             4rem, 1024 ≈ 180 px → 11rem, ab 1280 ist
                                             die Zeile unkritisch (~600 px Luft) →
                                             24rem. Wer Datum, Badge oder die Zellen-
                                             Polsterung ändert, verschiebt diese
                                             Budgets und misst neu (UT-096).
                                             Warum überhaupt so eng bei 768: die
                                             Fusszeilen "davon MWST" und "Total inkl.
                                             MWST" sind colspan-Zeilen, ihr WERT liegt
                                             immer in der letzten Spalte. Läuft die
                                             Tabelle über, verschwindet als Erstes das
                                             Total — der Kernzweck der Seite. Ein
                                             gekürzter Titel wiegt leichter als ein
                                             Total, das nur per Querscrollen sichtbar
                                             ist (dieselbe Abwägung wie B-3, Welle 2). -->
                                            <a
                                                :href="row.mod.link"
                                                :title="row.mod.title"
                                                class="flex-1 min-w-0 truncate max-w-[4rem] lg:max-w-[11rem] xl:max-w-[24rem] text-minplan-blue hover:underline"
                                                >{{ row.mod.title }}</a
                                            >
                                            <span
                                                class="shrink-0 px-1.5 py-0.5 rounded text-[10px] lg:text-xs font-medium"
                                                :class="badgeClass(row.mod.state)"
                                            >
                                                {{
                                                    $t(
                                                        `orderModifications.states.${row.mod.state}`
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </td>
                                    <td v-if="show('budget')"></td>
                                    <td
                                        v-if="show('mehrkosten')"
                                        class="px-2 py-1.5 whitespace-nowrap text-xs lg:text-sm text-gray-600 text-right"
                                        :class="detailContentClass(row)"
                                    >
                                        {{ fmt(modCost(row.mod)) }}
                                    </td>
                                    <td v-if="showDifference"></td>
                                    <!-- Thema 3 (Auftrag Luca, 23.09.2026): die
                                         Detailzeile traegt Gebuehr und Honorar
                                         selbst. Vorher standen beide nur summiert
                                         in der Themenzeile darueber, und die
                                         aufgeklappte Zeile liess die Spalten leer.
                                         Beide folgen dem MWST-Schalter der Tabelle
                                         wie die Spalte daneben — sonst stuende
                                         netto unter einer Themenzeile, die brutto
                                         zeigt (40.00 gegen 43.25). Eine nicht
                                         verrechnete Aenderung und der Budget-Modus
                                         liefern `null` und damit denselben Strich
                                         wie jede andere Zelle ohne Wert. -->
                                    <td
                                        v-if="show('gebuehr')"
                                        class="px-2 py-1.5 whitespace-nowrap text-xs lg:text-sm text-gray-600 text-right"
                                        :class="detailContentClass(row)"
                                    >
                                        {{ fmt(modFee(row.mod)) }}
                                    </td>
                                    <td
                                        v-if="show('honorar')"
                                        class="px-2 py-1.5 whitespace-nowrap text-xs lg:text-sm text-gray-600 text-right"
                                        :class="detailContentClass(row)"
                                    >
                                        {{ fmt(modConsultancyFee(row.mod)) }}
                                    </td>
                                    <!-- Auftrag Luca, 2026-08-25: die
                                         Detailzeile fuehrt KEINEN eigenen
                                         Total-Betrag — eine einzelne
                                         Bestellungsaenderung traegt weder
                                         Gebuehr noch Honorar, ihr "Total"
                                         waere ihr eigener Kostenbetrag und
                                         damit eine Dopplung der Zelle
                                         daneben. Die Zelle bleibt leer und
                                         haelt allein die Spaltenausrichtung;
                                         ohne sie verrutschen Detail- und
                                         Themenzeile gegeneinander. -->
                                    <td v-if="showTotalColumn" :class="stickyTotal"></td>
                                </template>
                            </tr>
                        </template>
                    </template>
                </tbody>
                <!-- GESTALTUNG DER FUSSZEILE (Entscheid Luca, 2026-08-26,
                     Variante A «Ruhiger Block»). Drei Dinge waren vorher
                     nicht in Ordnung:
                       * VIER Trennlinien auf rund 100 px — `tfoot` plus je
                         eine auf allen drei Zeilen. Die Zwischensumme braucht
                         ihre Abgrenzung von den Themen, die beiden darunter
                         gehoeren zu ihr und trennten sich ohne Grund
                         gegenseitig. Jetzt: EINE kraeftige Linie oben.
                       * ZWEI konkurrierende Fettungen — Zwischensumme und
                         Schlusszeile waren gleich stark ausgezeichnet, das
                         Auge fand den zu zahlenden Betrag nicht. Die
                         Zwischensumme ist eine Rechenzwischenstufe.
                       * Die DOPPELTE UNTERSTREICHUNG ist buchhalterisch
                         richtig, in einem sonst flaechigen Interface aber ein
                         Fremdkoerper — und sie sass direkt unter einer Linie.
                     Statt ihrer traegt der Abschlussbetrag eine Flaeche in
                     `minplan-beige`, der meistgenutzten Flaeche der Plattform
                     (69 Verwendungen). Eine Flaeche traegt ausserdem die
                     Distanz zwischen Etikett und Betrag, die seit UI-1
                     entstanden ist (~900 px bei 1440) — eine Linie tut das
                     nicht.
                     WELCHE ZEILE die Flaeche bekommt, entscheidet dieselbe
                     Frage wie zuvor die Unterstreichung: es gibt genau EINEN
                     Abschlussbetrag. Inklusive ist das die Zwischensumme,
                     exklusive die Schlusszeile — die Auszeichnung wandert
                     also mit dem MWST-Schalter, und das ist beabsichtigt. -->
                <!-- DIE ABGRENZUNGSLINIE SITZT AUF DEN ZELLEN, NICHT AUF
                     `<tfoot>` (UI-Gate, 2026-08-26, Blocker). Auf der Tabelle
                     steht `divide-y divide-gray-200`; dessen Selektor
                     `.divide-y > :not([hidden]) ~ :not([hidden])` hat die
                     Spezifitaet 0-3-0 und schlaegt jedes `border-*` am
                     `<tfoot>` (0-1-0). Ein `border-t-2 border-gray-300` dort
                     rendert deshalb als 1 px grau-200 — dieselbe Kante wie
                     zwischen zwei beliebigen Themenzeilen.
                     Das war nicht kosmetisch: mit der entfetteten
                     Zwischensumme begann der Fussbereich fuer das Auge
                     nirgends mehr, die Zeile war schwaecher ausgezeichnet als
                     die Zeilen, die sie summiert. Auf den ZELLEN greift
                     `divide-y` nicht — die Schlusszeile macht es im selben
                     `<tfoot>` seit jeher richtig vor. -->
                <tfoot>
                    <tr :class="abschlusszeileClass(!showGrandTotalRow)">
                        <td
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ zwischensummeLabel }}
                        </td>
                        <td
                            v-if="show('budget')"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ fmt(footerBudget) }}
                        </td>
                        <td
                            v-if="show('mehrkosten')"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ fmt(footerOffered) }}
                        </td>
                        <td
                            v-if="showDifference"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ fmt(footerMehrkosten) }}
                        </td>
                        <td
                            v-if="show('gebuehr')"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ fmt(footerFee) }}
                        </td>
                        <td
                            v-if="show('honorar')"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="zwischensummeClass"
                        >
                            {{ fmt(footerConsultancyFee) }}
                        </td>
                        <td
                            v-if="showTotalColumn"
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-right border-t-2 border-gray-400"
                            :class="[stickyTotal, zwischensummeClass]"
                        >
                            {{ fmt(footerTotalColumn) }}
                        </td>
                    </tr>
                    <!-- UI-1, Folgeentscheid (2026-08-25): die Beschriftung
                         steht in der ERSTEN Spalte, nicht mehr rechtsbuendig
                         unmittelbar links des Betrags. Grund ist die klebende
                         Total-Spalte: sie belegt das rechte Band der
                         Sichtflaeche dauerhaft, und was in der Tabelle direkt
                         links davon liegt, ist bei jedem Ueberlauf hinter ihr
                         — das UI-Gate hat genau das vorhergesagt («in der
                         Fusszeile verdeckt sie das Etikett davon MWST»).
                         Nebeneffekt: alle drei Fusszeilen beschriften sich
                         jetzt gleich, naemlich dort, wo schon
                         «Zwischensumme» steht. -->
                    <tr v-if="hasTaxTotal" class="bg-white">
                        <td
                            class="px-2 py-1.5 lg:py-2 whitespace-nowrap text-xs lg:text-sm text-gray-500"
                        >
                            {{
                                mwst
                                    ? $t("apartments.taxInclLabel")
                                    : $t("apartments.taxExclLabel")
                            }}
                        </td>
                        <td
                            v-if="visibleColumnCount > 2"
                            :colspan="visibleColumnCount - 2"
                            class="px-2 py-1.5 lg:py-2"
                        ></td>
                        <td
                            class="px-2 py-1.5 lg:py-2 whitespace-nowrap text-xs lg:text-sm text-gray-500 text-right"
                            :class="stickyTotal"
                        >
                            {{ fmt(total.totalColumnTax) }}
                        </td>
                    </tr>
                    <!-- Nacharbeit UI-2 (2026-08-25, Vorlage Luca): die
                         Schlusszeile der Exkl.-Ansicht. Inklusive ist die
                         Zwischensumme schon der zu zahlende Betrag; exklusive
                         stuende ohne diese Zeile nirgends, was der Kaeufer
                         ueberweist. -->
                    <tr v-if="showGrandTotalRow" :class="abschlusszeileClass(true)">
                        <td
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm font-bold text-gray-900 border-t border-gray-300"
                        >
                            {{ $t("apartments.totalInclVat") }}
                        </td>
                        <td
                            v-if="visibleColumnCount > 2"
                            :colspan="visibleColumnCount - 2"
                            class="px-2 py-2 lg:py-3 border-t border-gray-300"
                        ></td>
                        <td
                            class="px-2 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm font-bold text-gray-900 text-right border-t border-gray-300"
                            :class="stickyTotal"
                        >
                            {{ fmt(total.totalColumn) }}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script>
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import { defineComponent } from "vue";

// Themen 2-4 (Spec
// docs/superpowers/specs/2026-08-04-welle2-tabelle-detailebene-design.md):
// Spaltensatz Thema/Budget/Offeriert/Gebühr/Honorar (Differenz-Spalte
// entfällt), mwst-Prop steuert die Zellwahl cost/costExcl je Zeile und
// Fusszeile, aufklappbare Detailebene je Thema mit Status-Badges und
// Trenner-Weiche. Auf-/Zu-Zustand liegt in sessionStorage, gekeyt über die
// Topic-uuid (nicht Array-Index) plus die Wohnungs-uuid — überlebt damit
// Inertia-Navigation innerhalb derselben Browser-Session.
//
// Thema 1 (Spec docs/superpowers/specs/2026-08-04-welle2-spalten-sichtbarkeit-design.md):
// `visibleColumns`-Prop filtert Kopf-, Body- und Fusszeilen-Spalten über
// `show(key)`. Fehlende Prop (`null`/`undefined`) heisst "alle Spalten
// sichtbar" — das ist der Backend-Vertrag (Default = alle) UND der
// unveränderte Bestandszustand dieser Tabelle. Genullte Werte (`null`,
// serverseitige Sperre) rendern als "—" statt 0 oder leer über `fmt()` — das
// gilt für Zellen INNERHALB einer sichtbaren Spalte (Themen-Zeilen,
// Zwischensumme). Fix B-3 (UI-Gate Welle 2): die MwSt-/Total-Fusszeile ist
// je eine einzelne, spaltenübergreifende (`colspan`) Zeile — wird ihre
// Verbundsumme genullt, entfällt die ganze Zeile statt "—" zu zeigen.
// NACHGEZOGEN (2026-08-26): die beiden damals gemeinten Felder gibt es so
// nicht mehr — `subtotal`(Excl) ist mit Plan Task 7 entfallen, und der
// Waechter der MwSt-Zeile haengt seit der UI-2-Nacharbeit an
// `totalColumnTax` statt an `taxTotal`. Die REGEL ist unveraendert; nur die
// Felder, auf die sie zeigt, heissen anders (siehe `hasTaxTotal`).
//
// Darstellung der Detailebene (Angleich an das in der Spec bestätigte
// Zielbild, ANFORDERUNGEN-V2-Nachlauf): die Unterzeilen sind eingerückte
// Zeilen DIESER Tabelle (Datum · Titel · Badge in der Themen-Spalte, Betrag
// in der Offeriert-Zelle), nicht mehr eine verschachtelte Tabelle in einer
// colspan-Zelle. Der Betrag sitzt damit strukturell auf seiner Spalte; die
// dafür gebaute Colspan-Arithmetik (`detailContentColspan`/
// `detailFillerColspan`, Befund K-2) ist ersatzlos entfallen. Ist
// "mehrkosten" nicht sichtbar, entfällt allein die Betragszelle — Pfeil,
// Titel, Datum und Badge bleiben (bewusste Abweichung vom PoC, der den
// Pfeil dann ganz ausblendet).
export default defineComponent({
    components: { ChevronDownIcon, ChevronRightIcon },
    props: {
        apartment: { type: Object, required: true },
        topics: { type: Array, required: true },
        total: { type: Object, required: true },
        mwst: { type: Boolean, default: true },
        visibleColumns: { type: Array, default: null },
        // Auftrag Luca, 2026-08-17. Fehlt die Prop, bleibt die Tabelle wie
        // bisher — Offerten-Modus ist der Default.
        isBudgetBased: { type: Boolean, default: false },
    },
    data() {
        return {
            expanded: [],
            columnKeys: ["budget", "mehrkosten", "gebuehr", "honorar"],
        };
    },
    computed: {
        storageKey() {
            return `apartmentBudgetTable.expanded.${this.apartment?.uuid ?? "unknown"}`;
        },
        expandableTopics() {
            return this.topics.filter((topic) => this.hasModifications(topic));
        },
        allExpanded() {
            return (
                this.expandableTopics.length > 0 &&
                this.expandableTopics.every((topic) => this.isExpanded(topic))
            );
        },
        // Thema-Spalte (immer sichtbar) + Anzahl der aktuell sichtbaren
        // COLUMN_KEYS-Spalten — Grundlage für die dynamischen `colspan`e der
        // Fusszeilen (MwSt-/Total-Zeile) und der Trennerzeile der Detailebene.
        visibleColumnCount() {
            return (
                1 +
                this.columnKeys.filter((key) => this.show(key)).length +
                (this.showDifference ? 1 : 0) +
                (this.showTotalColumn ? 1 : 0)
            );
        },
        // Die Spalte hat bewusst KEINEN eigenen Schlüssel in
        // `Project::COLUMN_KEYS` und damit kein eigenes Häkchen in der
        // Spalten-Verwaltung: sie ist die Differenz ihrer beiden Nachbarn.
        // Wäre eine davon gesperrt, liesse sie sich aus den zwei
        // verbleibenden Zahlen zurückrechnen — genau deshalb nullt das
        // Backend `mehrkosten` schon heute unter exakt dieser Bedingung
        // (`ApartmentShowResource::nullLockedTopicRow`/`nullLockedTotals`).
        // Die Anzeige folgt derselben Regel, statt eine zweite zu erfinden.
        // Auftrag Luca, 2026-08-25, Entscheide 1.1 und 1.2: im budgetbasierten
        // Modus heisst die Spalte «Offeriert». Als Computed und nicht inline,
        // damit die Regel einmal im Script steht.
        offeredLabelKey() {
            return this.isBudgetBased ? "apartments.quotedCHF" : "apartments.offeredCHF";
        },
        showDifference() {
            return this.isBudgetBased && this.show("budget") && this.show("mehrkosten");
        },
        // Fix (C-Frontend-Review, Themen 2-4): die Fusszeile "Zwischensumme"
        // bindet die reinen Spaltensummen der Themenkosten (`costs`/
        // `costsExcl`), NICHT mehr `subtotal`(Excl) — das kombiniert
        // Kosten+Gebühren und dupliziert damit die Total-Zeile. Es gilt neu
        // sichtbar: Zwischensumme(Kosten) + Gebühr + Honorar = Total(subtotal).
        footerOffered() {
            return this.mwst ? this.total.costs : this.total.costsExcl;
        },
        // Netto-Umstellung 2026-08-18 (Spec
        // docs/superpowers/specs/2026-08-18-netto-berechnung-budget-design.md,
        // Entscheide 2 und 11): Budget und Differenz hoeren jetzt ebenfalls
        // auf den MWST-Umschalter. Bis dahin rendern beide Zellen ohne Zweig
        // — im Zustand "exkl." stand damit ein Brutto-Budget neben
        // Netto-Kosten, und die Differenz daneben passte zu keiner der
        // beiden Spalten.
        footerBudget() {
            return this.mwst ? this.total.budget : this.total.budgetExcl;
        },
        footerMehrkosten() {
            return this.mwst ? this.total.mehrkosten : this.total.mehrkostenExcl;
        },
        footerFee() {
            return this.mwst ? this.total.baseFee : this.total.baseFeeExcl;
        },
        footerConsultancyFee() {
            return this.mwst ? this.total.consultancyFee : this.total.consultancyFeeExcl;
        },
        // Klärung aus dem B-Review (Plan, Abschnitt "Klärungen aus dem
        // B-Review"): das Fusszeilen-Total bindet total.subtotal(Excl) — NICHT
        // total.totalMehrkosten.
        // Auftrag Luca, 2026-08-25, Entscheid 4.2: `footerTotal`/`hasFooterTotal`
        // sind entfallen. Die gespannte Fusszeile «Total inkl./exkl. MwSt»
        // trug im Budget-Modus eine ANDERE Zahl als die neue Spalte (sie
        // rechnete dort auf den vollen Kosten statt auf der Differenz) — zwei
        // Zahlen namens Total auf einem Bildschirm. Die Spalte hat die
        // Aufgabe übernommen, an der Stelle, wo man sie sucht.
        footerTotalColumn() {
            return this.mwst ? this.total.totalColumn : this.total.totalColumnExcl;
        },
        // UI-1 (UI-Gate, 2026-08-25, Befund UI-1 + Variante V2): die
        // Total-Spalte klebt am rechten Rand.
        //
        // GEMESSEN, NICHT GERATEN — die Zahlen stehen in
        // `docs/testing/reports/anzeige-paket-ui-report.md`, Abschnitt 1.6:
        // die Spalte kostet 89 px (Budget-Modus), der Container fasst bei
        // 1024 px 789 px und bei 768 px 525 px. Zwei Dinge, die man ohne
        // Messung falsch entschieden haette:
        //
        //  * Ein kuerzerer Spaltenkopf bringt EXAKT 0 px — die Breite haengt
        //    am Betrag, nicht an «TOTAL / inkl. MWST».
        //  * Die Polsterung (`lg:px-4` → `px-2`, gleich oben im Markup)
        //    loest 1024 vollstaendig (−112 px), wirkt bei 768 aber gar
        //    nicht: unterhalb von `lg` greift ohnehin schon die schmale
        //    Fassung. Fuer 768 blieben nur eine Spalte weniger oder diese
        //    hier.
        //
        // `bg-inherit` statt einer festen Farbe: jede ZEILE traegt ihre
        // Deckfarbe selbst (Kopf, Themenzeile, Detailzeilen, Fusszeilen), die
        // klebende Zelle uebernimmt sie. Ohne Deckung scrollen die Spalten
        // sichtbar unter ihr durch. Rand und Schatten links zeigen, dass hier
        // etwas ueberdeckt — sie stehen auch dann, wenn nichts ueberlaeuft,
        // und trennen dort die abgeleitete Summe von ihren Summanden.
        //
        // GATE-BEFUND B-2 (UI-Abnahme, 23.09.2026) — DIE SPALTE KLEBT ERST AB
        // `lg`, UNTERHALB DAVON SCROLLT SIE MIT.
        //
        // Gemessen mit echtem Chromium auf der gebauten `app.css`: sobald die
        // Tabelle breiter ist als ihr Behaelter, schiebt `right-0` die
        // klebende Zelle ueber ihre linke Nachbarin — bei 768 px um 59 px,
        // und das ist GENAU die Honorar-Spalte, die dieses Paket neu
        // befuellt. Der Betrag stand im DOM und war am Bildschirm nicht zu
        // lesen.
        //
        // Warum nicht «deckender machen» oder «schmaler»: das Ueberdecken ist
        // die Bauart einer klebenden Spalte und kein Fehler — sie MUSS
        // ueberdecken, sonst klebt sie nicht. Bei 768 px bleiben von 672 px
        // Behaelterbreite nach Abzug der Spalte rund 590 fuer fuenf Spalten;
        // dort kostet sie mehr, als sie bringt. Ab `lg` ist die Tabelle in
        // aller Regel breit genug, und wo sie es nicht ist, gibt das
        // Querscrollen die verdeckte Spalte am rechten Anschlag wieder frei.
        //
        // Der Nebeneffekt ist der eigentliche Gewinn: ohne klebende Zelle
        // laeuft der Ueberhang bei 768 px ungeschmaelert in den
        // `overflow-x-auto`-Behaelter — er scrollt, und jede Spalte ist
        // erreichbar.
        //
        // Rand, Schatten und Toenung bleiben auf ALLEN Breiten: sie zeichnen
        // die abgeleitete Summe gegen ihre Summanden ab, unabhaengig vom
        // Kleben.
        stickyTotalBase() {
            // SCHWELLE `xl`, NICHT `lg` (UI-Nachpruefung N-1, 24.09.2026).
            //
            // Bei `lg` (ab 1024 px) klebte die Total-Spalte ueber der
            // Honorar-Spalte und schnitt 10 px Betragstext ab: am Bildschirm
            // stand «421.5» statt «421.55», der Spaltentitel las sich
            // «HONORA». Eine abgeschnittene Zahl ist eine FALSCHE Zahl — und
            // der macOS-Scrollbalken erscheint erst beim Scrollen, es gibt
            // also keinen Hinweis darauf, dass da noch etwas steht.
            //
            // Gemessen wurde beides: bei 1024 Ueberdeckung 19 px (Text 10 px),
            // bei 768 nach dem Wechsel auf `static` Ueberdeckung 0 und der
            // Container scrollt 75 px — vollstaendig lesbar. `xl` uebertraegt
            // dieses belegte Verhalten auf 1024 bis 1279.
            //
            // Der Preis: zwischen 1024 und 1279 klebt die Total-Spalte nicht
            // mehr mit. Eine mitlaufende Spalte ist weniger wert als eine
            // lesbare Zahl.
            return "xl:sticky xl:right-0 xl:z-10 border-l border-gray-200 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]";
        },
        stickyTotal() {
            return `${this.stickyTotalBase} ${this.totalColumnTint}`;
        },
        /**
         * Die KOPFZELLE der Total-Spalte (Auftrag Luca, 2026-08-26,
         * Nachtrag): sie behaelt die Farbe der Kopfzeile, waehrend Koerper und
         * Fusszeilen die Toenung der Spalte tragen. Der Spaltentitel soll
         * aussehen wie die uebrigen Spaltentitel.
         *
         * Das ist nicht nur Geschmack: der `<th>` traegt beigen TEXT
         * (`text-minplan-beige`) auf der grauen Kopfzeile. Mit beiger Flaeche
         * darunter stuende Beige auf Beige, und der Titel verschwaende fast.
         *
         * `bg-inherit` und keine feste Farbe — die Deckung muss bleiben, sonst
         * scrollen die Spalten unter der klebenden Zelle durch; die Kopfzeile
         * liefert sie mit `bg-minplan-lightgreybeige` selbst.
         */
        stickyTotalHead() {
            return `${this.stickyTotalBase} bg-inherit`;
        },
        /**
         * Die Flaeche der GANZEN Total-Spalte (Auftrag Luca, 2026-08-26):
         * dieselbe Toenung wie der Abschlussbetrag unten, ueber Kopf, Themen-,
         * Detail- und Fusszeilen hinweg. Vorher nahm die Spalte per
         * `bg-inherit` die Farbe ihrer jeweiligen Zeile an und war dadurch
         * dreifarbig (Kopf grau, Zeilen weiss, Abschluss beige).
         *
         * Die Deckung bleibt die eigentliche Auflage des UI-Gates: die Spalte
         * KLEBT, und eine durchsichtige Zelle laesst die Spalten beim
         * Querscrollen sichtbar unter ihr durchlaufen. `bg-minplan-beige` ist
         * deckend — eine Alphafarbe oder `opacity` waere hier der Fehler.
         *
         * Der Rueckfall auf `bg-inherit` haelt K-F3 (UI-Gate, 2026-08-26)
         * ein: gibt es gar keine Total-Spalte, zeichnet die Toenung einen
         * Betrag aus, der nirgends steht. Dann bleibt alles wie bisher — die
         * beiden colspan-Fusszeilen rendern ihre rechte Zelle auch ohne
         * Total-Spalte.
         */
        totalColumnTint() {
            return this.showTotalColumn ? "bg-minplan-beige" : "bg-inherit";
        },
        /**
         * Auftrag Luca, 2026-08-26: in der Inkl.-Ansicht ist diese Zeile
         * bereits der zu zahlende Betrag (`showGrandTotalRow` haengt genau
         * daran) — «Zwischensumme» behauptet dort eine Rechenzwischenstufe,
         * die es nicht gibt. In der Exkl.-Ansicht bleibt es wie gehabt, denn
         * dort folgt darunter die echte Schlusszeile.
         *
         * Bewusst an `mwst` und NICHT an `showGrandTotalRow`: letzteres ist
         * auch dann falsch, wenn in der EXKL.-Ansicht kein Total vorliegt
         * (`showGrandTotalRow` = `!mwst && totalColumn != null`). Die Zeile
         * waere dann eine echte Zwischensumme und truege trotzdem die
         * Abschluss-Beschriftung.
         */
        zwischensummeLabel() {
            return this.mwst
                ? this.$t("apartments.totalInclVat")
                : this.$t("apartments.subtotal");
        },
        /**
         * Die Zellen der Zwischensummen-Zeile. Fett und schwarz NUR, wenn
         * diese Zeile der Abschlussbetrag ist (Inkl.-Ansicht) — sonst ist sie
         * eine Rechenzwischenstufe und tritt zurueck.
         */
        zwischensummeClass() {
            return this.showGrandTotalRow
                ? "font-medium text-gray-700"
                : "font-bold text-gray-900";
        },
        showTotalColumn() {
            const wert = this.mwst ? this.total.totalColumn : this.total.totalColumnExcl;

            return wert !== null && wert !== undefined;
        },
        // B-3 (UI-Gate Welle 2, welle2-ui-report.md): die MwSt-/Total-
        // Fusszeile ist je eine EINZELNE, über den ganzen sichtbaren
        // Spaltensatz gespannte (`colspan`) Zeile — anders als die
        // Zwischensumme, die pro Spalte eine eigene Zelle führt und daher
        // beim serverseitig genullten Einzelwert weiterhin korrekt "—"
        // zeigt (Thema-1-Vertrag, `fmt()`, unverändert). Ist der genullte
        // Wert einer Verbundsumme (heute `totalColumnTax`) hingegen
        // die GESAMTE Zeile, blendet ein "—" den Käufer im Kernzweck der
        // Seite ohne verständliche Antwort aus (B-3) — die Zeile entfällt
        // deshalb komplett, statt einen Gedankenstrich zu zeigen.
        // Nacharbeit UI-2 (2026-08-25, Vorlage Luca): die Schlusszeile gibt es
        // NUR in der Exkl.-Ansicht. Inklusive ist die Zwischensumme bereits
        // der zu zahlende Betrag — eine dritte Zeile waere dort eine
        // Wiederholung. Ohne sie stuende in der Exkl.-Ansicht dagegen
        // nirgends, was der Kaeufer ueberweist.
        showGrandTotalRow() {
            return (
                !this.mwst &&
                this.total.totalColumn !== null &&
                this.total.totalColumn !== undefined
            );
        },
        // BEGRUENDETE ANPASSUNG (2026-08-25, Nacharbeit zu UI-2): der Wachter
        // haengt an `totalColumnTax` — dem Wert, den diese Zeile ZEIGT — und
        // nicht mehr an `taxTotal`. Seit der UI-2-Runde rendert die Zeile
        // `totalColumnTax` (die MwSt DES TOTALS); der Waechter blieb auf
        // `taxTotal` stehen (die MwSt von Kosten+Gebuehren). Die beiden sind
        // nicht gemeinsam null: `taxTotal` wird NUR im Kaeufer-Zweig genullt,
        // fuer den Betreuer nie. Ein Betreuer mit `internal = ["budget"]` hat
        // damit kein Total (kein sichtbarer Summand, Entscheid 4.4), aber ein
        // `taxTotal` — und las eine Zeile «davon MWST —».
        //
        // Die Absicht von B-3 (Welle 2) bleibt woertlich erhalten: keine
        // Verbundsumme als Gedankenstrich, lieber gar keine Zeile. Sie haengt
        // nur am richtigen Feld.
        hasTaxTotal() {
            return (
                this.total.totalColumnTax !== null &&
                this.total.totalColumnTax !== undefined
            );
        },
    },
    mounted() {
        this.restoreExpanded();
    },
    methods: {
        // Thema 1: fehlende `visibleColumns`-Prop == alle Spalten sichtbar
        // (Backend-Vertrag: der Default-Fall liefert die Prop gar nicht).
        // Der Server liefert das Total je Zeile fertig gerechnet — die
        // Komponente rendert es nur (Spec §5.2, revidiert).
        rowTotal(topic) {
            return this.mwst ? topic.total : topic.totalExcl;
        },
        /**
         * Die Flaeche des Abschlussbetrags. `bg-minplan-beige` ist die
         * meistgenutzte Flaeche der Plattform — die klebende Total-Zelle
         * uebernimmt sie ueber `bg-inherit`, deshalb muss sie auf der ZEILE
         * sitzen und deckend sein.
         */
        abschlusszeileClass(istAbschluss) {
            // K-F3 (UI-Gate, 2026-08-26): die Toenung zeichnet den ZU
            // ZAHLENDEN BETRAG aus. Gibt es keinen — kein sichtbarer Summand,
            // also keine Total-Spalte (Entscheid 4.4) —, verspricht sie
            // etwas, das nirgends steht. Dann bleibt die Zeile weiss.
            return istAbschluss && this.showTotalColumn ? "bg-minplan-beige" : "bg-white";
        },
        show(key) {
            return !this.visibleColumns || this.visibleColumns.includes(key);
        },
        // Thema 1: serverseitig genullte Beträge (gesperrte Spalte) rendern
        // als Gedankenstrich — nicht als 0 oder leere Zelle. `0` bleibt
        // unverändert `0`, nur `null`/`undefined` wird ersetzt.
        fmt(value) {
            return value === null || value === undefined ? "—" : value;
        },
        hasModifications(topic) {
            return !!(topic.modifications && topic.modifications.length);
        },
        isExpanded(topic) {
            return this.expanded.includes(topic.uuid);
        },
        toggle(topic) {
            this.expanded = this.isExpanded(topic)
                ? this.expanded.filter((uuid) => uuid !== topic.uuid)
                : [...this.expanded, topic.uuid];
            this.persistExpanded();
        },
        toggleAll() {
            this.expanded = this.allExpanded
                ? []
                : this.expandableTopics.map((topic) => topic.uuid);
            this.persistExpanded();
        },
        persistExpanded() {
            window.sessionStorage?.setItem(
                this.storageKey,
                JSON.stringify(this.expanded)
            );
        },
        restoreExpanded() {
            const raw = window.sessionStorage?.getItem(this.storageKey);
            if (!raw) {
                return;
            }
            try {
                this.expanded = JSON.parse(raw);
            } catch (e) {
                this.expanded = [];
            }
        },
        rowsFor(topic) {
            const modifications = topic.modifications || [];
            return {
                billed: modifications.filter((mod) => mod.billed),
                unbilled: modifications.filter((mod) => !mod.billed),
            };
        },
        // Eine flache Liste der Detailzeilen in Darstellungsreihenfolge:
        // verrechnet, dann der Trenner (nur falls es nicht verrechnete gibt),
        // dann die nicht verrechneten (gedämpft). Als eigene Liste statt
        // zweier v-for-Blöcke, weil jede Zeile jetzt eine echte Zeile der
        // äusseren Tabelle ist und der Trenner dazwischenliegt.
        detailRows(topic) {
            const { billed, unbilled } = this.rowsFor(topic);
            const rows = billed.map((mod) => ({
                type: "modification",
                key: mod.link,
                mod,
                dimmed: false,
            }));

            if (unbilled.length) {
                rows.push({
                    type: "separator",
                    key: "separator",
                    label: this.separatorLabel(topic),
                });
                unbilled.forEach((mod) =>
                    rows.push({
                        type: "modification",
                        key: mod.link,
                        mod,
                        dimmed: true,
                    })
                );
            }

            return rows;
        },
        // Die Detailebene hebt sich als Block von den Themenzeilen ab
        // (leichte Tönung); nicht verrechnete Zeilen zählen nicht ins Total
        // und sind zusätzlich gedämpft.
        // UI-1, Folgeentscheid (2026-08-25): DECKENDE Farben statt
        // `bg-minplan-lightgreybeige/{10,20}` und `opacity-60` auf der Zeile.
        // Die klebende Total-Zelle uebernimmt den Zeilenhintergrund
        // (`bg-inherit`) und muss deckend sein; eine Alphafarbe liesse die
        // Spalten durchscrollen, und `opacity-60` auf dem `<tr>` haette die
        // Zelle samt ihrer Deckfarbe mit durchsichtig gemacht.
        //
        // Die drei Werte sind die AUSGERECHNETE Mischung ueber Weiss, nicht
        // neu gewaehlt — `#979694` ist `minplan-lightgreybeige`:
        //   10 %                → #f5f5f4
        //   20 %                → #eaeaea
        //   10 % bei 60 % Deckung → #f9f9f9   (gedaempfte Zeile)
        // Am Bildschirm aendert sich dadurch nichts; die Daempfung des TEXTES
        // wandert nach `detailContentClass()`.
        detailRowClass(row) {
            if (row.type === "separator") {
                return "bg-[#f5f5f4]";
            }
            return row.dimmed ? "bg-[#f9f9f9]" : "bg-[#eaeaea]";
        },
        /**
         * Die Daempfung nicht verrechneter Detailzeilen — frueher `opacity-60`
         * auf dem `<tr>`, siehe `detailRowClass()`.
         */
        detailContentClass(row) {
            return row.dimmed ? "opacity-60" : "";
        },
        // Trenner-Weiche: "— ABGELEHNT —", wenn ALLE unbilled-Einträge
        // declined sind, sonst "— NICHT BERÜCKSICHTIGT —" (z. B. created
        // dabei).
        separatorLabel(topic) {
            const unbilled = this.rowsFor(topic).unbilled;
            const allDeclined = unbilled.every((mod) => mod.state === "declined");
            return allDeclined
                ? this.$t("apartments.separatorDeclined")
                : this.$t("apartments.separatorUnbilled");
        },
        badgeClass(state) {
            return (
                {
                    approved: "bg-green-100 text-green-800",
                    readyForApproval: "bg-amber-100 text-amber-800",
                    declined: "bg-red-100 text-red-800",
                }[state] || "bg-gray-100 text-gray-800"
            );
        },
        // Netto-Umstellung 2026-08-18 (Entscheide 2 und 11) — dasselbe
        // Muster wie `rowCost()`/`rowFee()`. Der unsuffigierte Schluessel ist
        // der BRUTTO-Wert, `…Excl` das Netto-Pendant; die Voreinstellung des
        // Umschalters bleibt brutto, nach aussen aendert sich nichts.
        rowBudget(topic) {
            return this.mwst ? topic.budget : topic.budgetExcl;
        },
        rowMehrkosten(topic) {
            return this.mwst ? topic.mehrkosten : topic.mehrkostenExcl;
        },
        rowCost(topic) {
            return this.mwst ? topic.cost : topic.costExcl;
        },
        rowFee(topic) {
            return this.mwst ? topic.fee : topic.feeExcl;
        },
        rowConsultancyFee(topic) {
            return this.mwst ? topic.consultancyFee : topic.consultancyFeeExcl;
        },
        modCost(mod) {
            return this.mwst ? mod.cost : mod.costExcl;
        },
        // Thema 3 (Auftrag Luca, 23.09.2026) — dasselbe Paar-Muster wie
        // `rowFee()`/`rowConsultancyFee()` eine Ebene hoeher: der unsuffigierte
        // Schluessel ist brutto, `…Excl` das Netto-Pendant.
        modFee(mod) {
            return this.mwst ? mod.fee : mod.feeExcl;
        },
        modConsultancyFee(mod) {
            return this.mwst ? mod.consultancyFee : mod.consultancyFeeExcl;
        },
    },
});
</script>

<style scoped></style>
